import React from 'react';
import {
  WarningCircle,
  WifiSlash,
  LockKey,
  EnvelopeSimple,
  ShieldWarning,
  Clock,
  Warning,
  ArrowClockwise,
  X,
} from '@phosphor-icons/react';
import { AuthError, AUTH_ERROR_CODES } from '../../types/auth';
import { ErrorHandler } from '../../utils/errorHandler';

interface ErrorDisplayProps {
  error: AuthError | null;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  /** Optional contextual action (e.g., a recovery link) rendered beneath the message. */
  action?: React.ReactNode;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  onDismiss,
  className = '',
  action,
}) => {
  if (!error) return null;

  const isRetryable = ErrorHandler.isRetryable(error);
  const errorMessage = ErrorHandler.getErrorMessage(error);

  const getErrorIcon = (code: string) => {
    const iconProps = { size: 20, weight: 'fill' as const, 'aria-hidden': true };
    switch (code) {
      case AUTH_ERROR_CODES.NETWORK_ERROR:
      case AUTH_ERROR_CODES.CONNECTION_TIMEOUT:
        return <WifiSlash {...iconProps} />;
      case AUTH_ERROR_CODES.INVALID_CREDENTIALS:
        return <LockKey {...iconProps} />;
      case AUTH_ERROR_CODES.ACCOUNT_LOCKED:
      case AUTH_ERROR_CODES.ACCOUNT_DISABLED:
        return <ShieldWarning {...iconProps} />;
      case AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED:
        return <EnvelopeSimple {...iconProps} />;
      case AUTH_ERROR_CODES.INSUFFICIENT_PERMISSIONS:
      case AUTH_ERROR_CODES.ROLE_REQUIRED:
      case AUTH_ERROR_CODES.PERMISSION_DENIED:
        return <Warning {...iconProps} />;
      case AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS:
      case AUTH_ERROR_CODES.RATE_LIMIT_EXCEEDED:
        return <Clock {...iconProps} />;
      default:
        return <WarningCircle {...iconProps} />;
    }
  };

  const getErrorTitle = (code: string) => {
    switch (code) {
      case AUTH_ERROR_CODES.NETWORK_ERROR:
        return 'Problème de connexion';
      case AUTH_ERROR_CODES.CONNECTION_TIMEOUT:
        return 'Délai dépassé';
      case AUTH_ERROR_CODES.INVALID_CREDENTIALS:
        return 'Identifiants incorrects';
      case AUTH_ERROR_CODES.ACCOUNT_LOCKED:
        return 'Compte verrouillé';
      case AUTH_ERROR_CODES.ACCOUNT_DISABLED:
        return 'Compte désactivé';
      case AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED:
        return 'Email non vérifié';
      case AUTH_ERROR_CODES.INSUFFICIENT_PERMISSIONS:
        return 'Accès refusé';
      case AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS:
      case AUTH_ERROR_CODES.RATE_LIMIT_EXCEEDED:
        return 'Trop de tentatives';
      case AUTH_ERROR_CODES.INTERNAL_ERROR:
      case AUTH_ERROR_CODES.SERVER_UNAVAILABLE:
      case AUTH_ERROR_CODES.DATABASE_ERROR:
        return 'Erreur serveur';
      default:
        return 'Erreur';
    }
  };

  return (
    <div
      className={`auth-alert auth-alert-error ${onDismiss ? 'pr-12' : ''} ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <span className="auth-alert-icon">{getErrorIcon(error.code)}</span>

      <div className="auth-alert-content">
        <p className="auth-alert-title">{getErrorTitle(error.code)}</p>
        <p className="auth-alert-body">{errorMessage}</p>

        {action && <div className="auth-alert-action">{action}</div>}

        {isRetryable && onRetry && (
          <div className="auth-alert-action">
            <button
              type="button"
              onClick={onRetry}
              className="auth-link inline-flex items-center gap-2 text-sm"
            >
              <ArrowClockwise size={16} weight="bold" aria-hidden />
              Réessayer
            </button>
          </div>
        )}
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-red-700/60 transition-colors hover:bg-red-900/10 hover:text-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
          aria-label="Fermer le message"
        >
          <X size={16} weight="bold" aria-hidden />
        </button>
      )}
    </div>
  );
};

export default ErrorDisplay;
